import s from './MainChatsView.module.css'
import { useInput } from '@/hooks'
import Search from '@UI/Search'
import Button from '@UI/Button'
import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { AddNewChatIcon, CloseIcon, EditIcon } from '@/utils/icons'
import { chatApi, usersAPI } from '@/api'
import ChatViewList from '@common/Chat/components/ChatViewList'
import { useAsyncRetry, useClickAway } from 'react-use'
import UserList from '@common/Chat/components/UserList'
import { useDispatch, useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import rolesEnum from '@/constant/rolesEnum'
import { useAddAlert } from '@/hooks/useAddAlert'
import useIsTechSupport from '../SupportChatButton/useIsTechSupport'
import { useChatContext } from '../ChatContext'
import cn from 'classnames'
import Preloader from '@UI/Preloader'
import debounce from "lodash.debounce";
import { openModal } from '@store/modalsSlise'
import TYPES from '@modals/TYPES'
import { CHAT_FOLDER_ALL_ID, useSelectedFolder } from './hooks/useSelectedFolder'
import { useFolders } from './hooks/useFolders'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import classNames from 'classnames'
import { useCancelToken } from '@/hooks/useCancelToken'
import axios from 'axios'
import websocket from '@/utils/websocket'
import useRoles from '@/hooks/useRoles'
import useTeacherCanInitiateChat from '@/hooks/useTeacherCanInitiateChat'
import { formateName } from '@/helpers/formateName'
import CompactSelect from '../components/CompactSelect/CompactSelect'
import {
  SUPPORT_TICKET_ASSIGNMENT_ALL,
  SUPPORT_TICKET_ASSIGNMENT_DEFAULT,
  SUPPORT_TICKET_ASSIGNMENT_MINE,
  SUPPORT_TICKET_ASSIGNMENT_UNASSIGNED,
  SUPPORT_TICKET_MANAGER_PREFIX,
  getSupportTicketFromEvent,
  getSupportTicketFilterParams,
} from '../helpers/supportTickets'
import { useHistory, Link } from 'react-router-dom'
import {
  fetchLessonChatsPage,
  withLessonChatContext,
} from '@/helpers/lessonChats'
import { formatCrmChatUnreadBadge } from '@pages/Admin/CRM/utils/leadFields'

const LESSON_CHAT_FILTER_ALL = 'all'
const EMPTY_LESSON_CHAT_FILTERS = { courses: [], blocks: [], themes: [], lessons: [], groups: [] }
const LESSON_CHAT_KIND_OPTIONS = [
  { value: LESSON_CHAT_FILTER_ALL, label: 'Все' },
  { value: 'general', label: 'Общие чаты курса' },
  { value: 'group', label: 'Групповые чаты' },
]

// Курс — это направление, блок — раздел внутри него. В селектах показываем оба уровня.
const toFilterOptions = (allLabel, items = []) => ([
  { value: LESSON_CHAT_FILTER_ALL, label: allLabel },
  ...items.map((item) => ({ value: item.id, label: item.name })),
])

const childrenOf = (items = [], parentKey, selectedParentId, visibleParents) => {
  if (selectedParentId !== LESSON_CHAT_FILTER_ALL) {
    return items.filter((item) => Number(item[parentKey]) === Number(selectedParentId))
  }

  const parentIds = new Set(visibleParents.map((parent) => Number(parent.id)))

  return items.filter((item) => parentIds.has(Number(item[parentKey])))
}
// Стабильная ссылка: значение по умолчанию попадает в зависимости useMemo.
const EMPTY_UNREAD_CHATS = []

const parseTechSupportUsers = (value) => {
  try {
    const users = value ? JSON.parse(value) : []
    return Array.isArray(users) ? users : []
  } catch {
    return []
  }
}

const getChatUnreadCount = (chat) => Number(
  chat?.unreadCount
  ?? chat?.unread_count
  ?? chat?.count_unread
  ?? chat?.countUnread
  ?? chat?.supportTicket?.unreadCount
  ?? chat?.supportTicket?.unread_count
  ?? 0
) || 0

const getChatLastActivityTime = (chat) => {
  const value = chat?.lastMessage?.createdAt
    || chat?.lastMessage?.created_at
    || chat?.last_message?.created_at
    || chat?.lastMessageAt
    || chat?.last_message_at
    || chat?.updatedAt
    || chat?.updated_at
    || chat?.createdAt
    || chat?.created_at
  const time = value ? new Date(value).getTime() : 0
  return Number.isNaN(time) ? 0 : time
}

// Непрочитанность берём и из строки чата, и из живого снимка get-active-users:
// ручная пометка «непрочитано» и только что пришедшее сообщение видны только во втором.
const isChatUnread = (chat, unreadChatIds) => getChatUnreadCount(chat) > 0
  || Boolean(unreadChatIds && unreadChatIds.has(Number(chat?.id)))

// Тот же порядок, что отдаёт бэкенд: закреплённые → непрочитанные → последняя активность.
const sortChatsUnreadFirst = (items = [], unreadChatIds = null) => [...items].sort((first, second) => {
  const fixedDiff = Number(Boolean(second?.isFixed)) - Number(Boolean(first?.isFixed))
  if (fixedDiff) return fixedDiff
  const unreadDiff = Number(isChatUnread(second, unreadChatIds)) - Number(isChatUnread(first, unreadChatIds))
  if (unreadDiff) return unreadDiff
  return getChatLastActivityTime(second) - getChatLastActivityTime(first)
})

const updateChatLastMessage = (items = [], update, unreadChatIds = null) => {
  if (update?.chatId === undefined || update?.message === undefined) return items

  let hasMatchedChat = false
  const nextChats = items.map((chat) => {
    if (Number(chat?.id) !== Number(update.chatId)) return chat

    hasMatchedChat = true
    return {
      ...chat,
      ...(chat?.type !== 'default' && !chat?.name && update?.title
        ? { name: update.title }
        : {}),
      lastMessage: {
        ...chat?.lastMessage,
        ...(update?.id === undefined ? {} : { id: update.id }),
        message: update.message,
        createdAt: update.createdAt || update.created_at || new Date().toISOString(),
        authorAvatar: update.authorAvatar ?? update.author_avatar ?? chat?.lastMessage?.authorAvatar,
      },
    }
  })

  return hasMatchedChat ? sortChatsUnreadFirst(nextChats, unreadChatIds) : items
}

const MainChatPage = ({
  setPage,
  activeUsers,
  unReadChats,
  lessonUnReadChats = EMPTY_UNREAD_CHATS,
  params,
  getActiveUsers,
  isFullPage = false,
  belowTabsSlot = null,
  integrationListSlot = null,
  isIntegrationListActive = false,
  showChatListModeTabs = false,
  chatListMode = 'internal',
  onChatListModeChange,
  integrationUnreadTotal = 0,
  integrationHasConnectedProviders = true,
  onPlatformTabSelect,
}) => {
  const history = useHistory()
  const {
    isAdmin,
    isJuniorAdmin,
    isPersonalManager,
    isSeminarian,
    isSeniorSeminarian,
  } = useRoles();
  const userId = useSelector(selectProfile)?.id
  const { isTechSupport, chatIsDisabledButTechnicalSupportIsEnabled, technicalSupportIsEnabled } = useIsTechSupport()
  const dispatch = useDispatch()
  const addAlert = useAddAlert()
  const { getCancelTokenSource: getChatListCancelTokenSource } = useCancelToken()
  const { getCancelTokenSource: getSearchCancelTokenSource } = useCancelToken()

  const [bindSearch, valueSearch, reset] = useInput('')
  const [users, setUsers] = useState([])
  const [isShowUsers, setIsShowUsers] = useState(false)
  const userRef = useRef(null)
  const canSeeLessonChats = isAdmin || isJuniorAdmin || isPersonalManager || isSeminarian || isSeniorSeminarian
  const initialTab = params?.selectedTab === 'lesson' && canSeeLessonChats
    ? 'lesson'
    : (chatIsDisabledButTechnicalSupportIsEnabled || params?.selectedTab === 'support') ? 'support' : 'all';

  const [selectedTab, setSelectedTab] = useState(initialTab) // 'all', 'direct', 'group', 'support', 'notification', 'lesson'
  const [hasLessonChats, setHasLessonChats] = useState(false)
  const [lessonChatsFilters, setLessonChatsFilters] = useState(EMPTY_LESSON_CHAT_FILTERS)
  const [selectedLessonChatKind, setSelectedLessonChatKind] = useState(LESSON_CHAT_FILTER_ALL)
  const [selectedLessonCourseId, setSelectedLessonCourseId] = useState(LESSON_CHAT_FILTER_ALL)
  const [selectedLessonBlockId, setSelectedLessonBlockId] = useState(LESSON_CHAT_FILTER_ALL)
  const [selectedLessonThemeId, setSelectedLessonThemeId] = useState(LESSON_CHAT_FILTER_ALL)
  const [selectedLessonLessonId, setSelectedLessonLessonId] = useState(
    params?.selectedTab === 'lesson' && params?.lessonId
      ? params.lessonId
      : LESSON_CHAT_FILTER_ALL,
  )
  const [selectedLessonGroupId, setSelectedLessonGroupId] = useState(LESSON_CHAT_FILTER_ALL)
  const profile = useSelector(selectProfile)
  const isChatsCanBeGroupedIntoFolders = useSelector(store => store?.settings?.isChatsCanBeGroupedIntoFolders === '1')
  const notificationChatsEnabled = useSelector(store => store?.settings?.isNotificationChatsEnabled === '1')
  const [hasNotificationChats, setHasNotificationChats] = useState(false)
  const notificationChatsSectionTitle = useSelector(store => (
    store?.settings?.isNotificationChatsCustomTitleEnabled === '1'
      ? store?.settings?.notificationChatsSectionTitle || 'Уведомления'
      : 'Уведомления'
  ))

  useEffect(() => {
    if (!notificationChatsEnabled) {
      setHasNotificationChats(false)
      return
    }

    let active = true
    chatApi.getChatList({ 'per-page': 1, page: 1, type: 'notification' })
      .then((response) => {
        if (!active) return
        const chats = response?.data?.data ?? []
        setHasNotificationChats(chats.length > 0)
      })
      .catch(() => active && setHasNotificationChats(false))

    return () => { active = false }
  }, [notificationChatsEnabled])
  const isUsersCanSearchByEmailInChat = useSelector(store => store?.settings?.isUsersCanSearchByEmailInChat === '1')
  const isUsersCanSearchByPhoneInChat = useSelector(store => store?.settings?.isUsersCanSearchByPhoneInChat === '1')
  const techSupportUsersSetting = useSelector(store => store?.settings?.techSupportUsers)
  const techSupportUserIds = useMemo(() => parseTechSupportUsers(techSupportUsersSetting), [techSupportUsersSetting])
  const directSearchPlaceholder = isUsersCanSearchByEmailInChat && isUsersCanSearchByPhoneInChat
    ? 'Введите ФИО, почту или телефон'
    : isUsersCanSearchByEmailInChat
      ? 'Введите ФИО или почту'
      : isUsersCanSearchByPhoneInChat
        ? 'Введите ФИО или телефон'
        : 'Введите ФИО собеседника'

  const chatContext = useChatContext()
  const lastMessageUpdate = chatContext?.lastMessageUpdate

  // Непрочитанные чаты уроков приходят отдельным ключом: в общий счётчик платформы
  // они намеренно не входят, но бейдж и пункт меню в разделе им нужны.
  const activeUnReadChats = selectedTab === 'lesson' ? lessonUnReadChats : unReadChats
  const unreadChatIds = useMemo(
    () => new Set((activeUnReadChats || []).map(chat => Number(chat?.chatId))),
    [activeUnReadChats],
  )

  const lessonFilterParams = useMemo(() => ({
    ...(selectedLessonChatKind !== LESSON_CHAT_FILTER_ALL ? { lesson_chat_kind: selectedLessonChatKind } : {}),
    ...(selectedLessonCourseId !== LESSON_CHAT_FILTER_ALL ? { course_id: selectedLessonCourseId } : {}),
    ...(selectedLessonBlockId !== LESSON_CHAT_FILTER_ALL ? { block_id: selectedLessonBlockId } : {}),
    ...(selectedLessonThemeId !== LESSON_CHAT_FILTER_ALL ? { theme_id: selectedLessonThemeId } : {}),
    ...(selectedLessonLessonId !== LESSON_CHAT_FILTER_ALL ? { lesson_id: selectedLessonLessonId } : {}),
    ...(selectedLessonGroupId !== LESSON_CHAT_FILTER_ALL ? { group_id: selectedLessonGroupId } : {}),
  }), [
    selectedLessonChatKind,
    selectedLessonCourseId,
    selectedLessonBlockId,
    selectedLessonThemeId,
    selectedLessonLessonId,
    selectedLessonGroupId,
  ])

  // Вкладка показывается, только если чаты уроков вообще есть: одна лёгкая проверка.
  useEffect(() => {
    if (!canSeeLessonChats) {
      setHasLessonChats(false)
      return
    }

    let cancelled = false
    fetchLessonChatsPage({ page: 1, perPage: 1 })
      .then(({ meta }) => {
        if (!cancelled) setHasLessonChats(Number(meta?.totalCount ?? 0) > 0)
      })
      .catch(() => {
        if (!cancelled) setHasLessonChats(false)
      })

    return () => {
      cancelled = true
    }
  }, [canSeeLessonChats])

  useEffect(() => {
    if (selectedTab === 'lesson' && !canSeeLessonChats) {
      setSelectedTab('all')
    }
  }, [canSeeLessonChats, selectedTab])

  useEffect(() => {
    if (selectedTab === 'support' && !isTechSupport) {
        setSelectedTab('all')
    }
    if(chatIsDisabledButTechnicalSupportIsEnabled && selectedTab !== 'support' && isTechSupport) {
        setSelectedTab('support')
    }
  }, [selectedTab, isTechSupport, chatIsDisabledButTechnicalSupportIsEnabled])

  const handlePlatformTabSelect = useCallback((tab) => {
    setSelectedTab(tab)
    onPlatformTabSelect?.(tab)
  }, [onPlatformTabSelect])

  const isIntegrationMode = showChatListModeTabs
    ? chatListMode === 'integration'
    : isIntegrationListActive

  const integrationUnreadBadge = useMemo(
    () => formatCrmChatUnreadBadge(integrationUnreadTotal),
    [integrationUnreadTotal],
  )

  const [initialLoad, setInitialLoad] = useState(true)

  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.allSettled([
        getActiveUsers(),
        getFolders(),
      ])
      setInitialLoad(false)
    }
    loadInitialData()
  }, [])

  const [chats, setChats] = useState([])
  const [supportStatuses, setSupportStatuses] = useState([])
  const [supportManagers, setSupportManagers] = useState([])
  const [selectedSupportStatusId, setSelectedSupportStatusId] = useState('all')
  const [selectedSupportTicketFilter, setSelectedSupportTicketFilter] = useState(SUPPORT_TICKET_ASSIGNMENT_DEFAULT)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  
  const { folders, getFolders, loadedFolders } = useFolders();

  const {selectedFolderId, setSelectedFolder} = useSelectedFolder({folders, loadedFolders});

  const loadSupportStatuses = async () => {
    const response = await chatApi.getSupportChatStatuses()

    if (response?.data?.status === 'success') {
      setSupportStatuses(Array.isArray(response?.data?.data) ? response.data.data : [])
      return
    }

    addAlert({ type: 'error', title: 'Не удалось загрузить статусы чатов' })
  }

  const loadSupportManagers = async () => {
    if (!techSupportUserIds.length) {
      setSupportManagers([])
      return
    }

    const response = await usersAPI.getUsers({
      filter: ['[role][neq]=children'],
      pageSize: false,
    })

    const users = Array.isArray(response?.data?.data) ? response.data.data : []

    setSupportManagers(
      users.filter((user) => techSupportUserIds.some((id) => Number(id) === Number(user?.id)))
    )
  }

  // Функция для загрузки чатов с пагинацией
  const loadChats = async ({page = 1, isLoadMore = false}) => {
    if (initialLoad || isLoadingMore) return

    const cancelTokenSource = getChatListCancelTokenSource()

    try {
      if (isLoadMore) {
        setIsLoadingMore(true)
      }

      const response = selectedTab === 'lesson'
        ? await chatApi.getLessonChats({
            'per-page': 20,
            page,
            ...lessonFilterParams,
          }, cancelTokenSource)
        : (selectedTab === 'support' && isTechSupport)
        ? await chatApi.getSupportChats({
            'per-page': 20,
            page,
            ...getSupportTicketFilterParams(selectedSupportTicketFilter),
            ...(selectedSupportStatusId !== 'all' ? { status_id: selectedSupportStatusId } : {})
          }, cancelTokenSource)
        : await chatApi.getChatList({
            'per-page': 20,
            page: page,
            type: selectedTab || 'all',
            ...(selectedFolderId !== CHAT_FOLDER_ALL_ID ? { folder_id: selectedFolderId } : {})
          }, cancelTokenSource)

      const responseLessonChatFilters = selectedTab === 'lesson'
        ? response?.data?._meta?.filters ?? EMPTY_LESSON_CHAT_FILTERS
        : EMPTY_LESSON_CHAT_FILTERS

      if (selectedTab === 'lesson') {
        setLessonChatsFilters(responseLessonChatFilters)
      }

      if (response?.data?.data) {
        const responseData = selectedTab === 'lesson'
          ? response.data.data.map((chat) => (
              withLessonChatContext(chat, responseLessonChatFilters)
            ))
          : response.data.data
        const responseChats = updateChatLastMessage(
          sortChatsUnreadFirst(responseData || [], unreadChatIds),
          lastMessageUpdate,
          unreadChatIds,
        )
        if (isLoadMore) {
          // Чат мог попасть в список раньше своей страницы (через chat-list-by-ids) — не дублируем.
          setChats(prev => sortChatsUnreadFirst([
            ...prev,
            ...responseChats.filter((chat) => !prev.some((el) => Number(el?.id) === Number(chat?.id))),
          ], unreadChatIds))
        } else {
          setChats(responseChats)
          setCurrentPage(1)
        }
        
        setHasMore(response?.data?._meta?.pageCount !== page)
        
        if (isLoadMore) {
          setCurrentPage(page)
        }
      }
    }
    catch (error) {
      if (!axios.isCancel(error)) {
        setHasMore(false)
      }
    }
    finally {
      if (isLoadMore) {
        setIsLoadingMore(false)
      }
    }
  }

  useEffect(() => {
    loadChats({page: 1})
  }, [selectedTab, selectedFolderId, initialLoad, selectedSupportStatusId, selectedSupportTicketFilter, isTechSupport, lessonFilterParams])

  // Пришло новое сообщение или чат пометили (не)прочитанным — порядок пересобираем сразу,
  // не дожидаясь перезагрузки списка.
  useEffect(() => {
    setChats((prev) => sortChatsUnreadFirst(prev, unreadChatIds))
  }, [unreadChatIds])

  useEffect(() => {
    if (selectedTab === 'support' && isTechSupport) {
      loadSupportStatuses()
      loadSupportManagers()
      return
    }

    setSelectedSupportStatusId('all')
    setSelectedSupportTicketFilter(SUPPORT_TICKET_ASSIGNMENT_DEFAULT)
  }, [selectedTab, isTechSupport, techSupportUserIds])

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      loadChats({page: currentPage + 1, isLoadMore: true})
    }
  }

  // После пометки (не)прочитанным порядок пересчитывает бэкенд: только он знает
  // и ручную пометку, и реальные непрочитанные.
  const handleReadStateChange = async () => {
    await getActiveUsers?.()
    loadChats({ page: 1 })
  }

  // Внутри чата урока вкладка должна сохраняться при возврате в список.
  const openChatFromList = (page, chatId, typeAction, pageParams) => setPage(
    page,
    chatId,
    typeAction,
    pageParams ?? (selectedTab === 'lesson'
      ? {
          selectedTab: 'lesson',
          ...(selectedLessonLessonId !== LESSON_CHAT_FILTER_ALL
            ? { lessonId: selectedLessonLessonId }
            : {}),
        }
      : undefined),
  )

  // Каскад: курс → блок → тема → урок; группы принадлежат курсу.
  const visibleLessonCourses = lessonChatsFilters.courses || []

  const visibleLessonBlocks = useMemo(
    () => childrenOf(lessonChatsFilters.blocks, 'courseId', selectedLessonCourseId, visibleLessonCourses),
    [lessonChatsFilters, selectedLessonCourseId, visibleLessonCourses],
  )

  const visibleLessonThemes = useMemo(
    () => childrenOf(lessonChatsFilters.themes, 'blockId', selectedLessonBlockId, visibleLessonBlocks),
    [lessonChatsFilters, selectedLessonBlockId, visibleLessonBlocks],
  )

  const visibleLessonLessons = useMemo(
    () => childrenOf(lessonChatsFilters.lessons, 'themeId', selectedLessonThemeId, visibleLessonThemes),
    [lessonChatsFilters, selectedLessonThemeId, visibleLessonThemes],
  )

  const visibleLessonGroups = useMemo(
    () => childrenOf(lessonChatsFilters.groups, 'courseId', selectedLessonCourseId, visibleLessonCourses),
    [lessonChatsFilters, selectedLessonCourseId, visibleLessonCourses],
  )

  const lessonCourseOptions = useMemo(
    () => toFilterOptions('Все курсы', visibleLessonCourses), [visibleLessonCourses])
  const lessonBlockOptions = useMemo(
    () => toFilterOptions('Все блоки', visibleLessonBlocks), [visibleLessonBlocks])
  const lessonThemeOptions = useMemo(
    () => toFilterOptions('Все темы', visibleLessonThemes), [visibleLessonThemes])
  const lessonLessonOptions = useMemo(
    () => toFilterOptions('Все уроки', visibleLessonLessons), [visibleLessonLessons])
  const lessonGroupOptions = useMemo(
    () => toFilterOptions('Все группы', visibleLessonGroups), [visibleLessonGroups])

  const resetLessonFilters = () => {
    setSelectedLessonChatKind(LESSON_CHAT_FILTER_ALL)
    setSelectedLessonCourseId(LESSON_CHAT_FILTER_ALL)
    setSelectedLessonBlockId(LESSON_CHAT_FILTER_ALL)
    setSelectedLessonThemeId(LESSON_CHAT_FILTER_ALL)
    setSelectedLessonLessonId(LESSON_CHAT_FILTER_ALL)
    setSelectedLessonGroupId(LESSON_CHAT_FILTER_ALL)
  }

  // Сбрасывать нечего, пока все селекты в «Все …» — тогда и кнопку не показываем.
  const activeLessonFiltersCount = Object.keys(lessonFilterParams).length

  const handleSelectLessonCourse = (value) => {
    setSelectedLessonCourseId(value)
    setSelectedLessonBlockId(LESSON_CHAT_FILTER_ALL)
    setSelectedLessonThemeId(LESSON_CHAT_FILTER_ALL)
    setSelectedLessonLessonId(LESSON_CHAT_FILTER_ALL)
    setSelectedLessonGroupId(LESSON_CHAT_FILTER_ALL)
  }

  const handleSelectLessonBlock = (value) => {
    setSelectedLessonBlockId(value)
    setSelectedLessonThemeId(LESSON_CHAT_FILTER_ALL)
    setSelectedLessonLessonId(LESSON_CHAT_FILTER_ALL)
  }

  const handleSelectLessonTheme = (value) => {
    setSelectedLessonThemeId(value)
    setSelectedLessonLessonId(LESSON_CHAT_FILTER_ALL)
  }

  const getChatListByIds = async () => {
    const response = await chatApi.getChatListByIds({ 
        chatIds: unReadChats?.map(el => el?.chatId),
        type: selectedTab || 'all',
    })
    if(response?.data?.data){
      const rows = response.data.data
      setChats(
        (prev) => {
          const updated = prev?.map((el) => rows.find(elem => elem?.id == el.id) ?? el) ?? []
          // Чата нет в списке — пришло сообщение в переписку, удалённую «только у меня», или в новую:
          // показываем его, если открыт общий список без папки (строка уже отфильтрована по вкладке).
          const appeared = selectedFolderId === CHAT_FOLDER_ALL_ID
            ? rows.filter((row) => !updated.some((el) => Number(el?.id) === Number(row?.id)))
            : []

          return sortChatsUnreadFirst([...appeared, ...updated], unreadChatIds)
        }
      )
    }
  } 

  const updateSupportTicketFromEvent = (ticketEvent) => {
    if (!ticketEvent?.chatId) return

    setChats((prev) => sortChatsUnreadFirst(prev.map((chat) => {
      if (Number(chat?.id) !== Number(ticketEvent.chatId)) {
        return chat
      }

      return {
        ...chat,
        supportTicket: getSupportTicketFromEvent(ticketEvent, chat?.supportTicket),
        supportStatus: ticketEvent?.supportStatus ?? chat?.supportStatus,
      }
    })))
  }

  useEffect(()=>{
      if (selectedTab === 'support' || selectedTab === 'lesson') return
      getChatListByIds();
  }, [unReadChats, selectedTab])

  const handleChangeSupportStatus = async (chatId, statusId) => {
    const response = await chatApi.setSupportChatStatus(chatId, {
      status_id: statusId,
    })

    if (response?.data?.status !== 'success') {
      addAlert({ type: 'error', title: 'Не удалось изменить статус чата' })
      return
    }

    const nextSupportStatus = statusId === 0
      ? null
      : supportStatuses.find((status) => Number(status?.id) === Number(statusId)) ?? null

    setChats((prev) => prev.map((chat) => (
      Number(chat?.id) === Number(chatId)
        ? {
            ...chat,
            supportStatus: response?.data?.data?.supportStatus ?? nextSupportStatus,
          }
        : chat
    )))

    loadChats({ page: 1 })

    if (isShowUsers) {
      debouncedGetUsers(valueSearch, selectedTab)
    }
  }

  const settings = useSelector((store) => store.settings)
  const teacherCanInitiateChat = useTeacherCanInitiateChat()

    const canStartChat = () => {
        const childrenCanStartChat = settings.isChildrenCanStartChat === '1'
        const parentCanStartChat = settings.isParentCanStartChat === '1'

        if (!teacherCanInitiateChat) return false
        if (profile.role === rolesEnum.children) return childrenCanStartChat
        if (profile.role === rolesEnum.parent) return parentCanStartChat

        return true
    }

  useClickAway(userRef, () => {
    setIsShowUsers(false)
  })

  const [loadingSearch, setLoadingSearch] = useState(false);

  const debouncedGetUsers = useMemo(
    () => {
      let requestCount = 0;
      return (
        debounce((searchValue, tab) => {
          const currentRequest = ++requestCount;
          setLoadingSearch(true);
          const cancelTokenSource = getSearchCancelTokenSource()

          chatApi
            .getUsersChatsList({
              search: searchValue.trim(),
              type: tab || 'all',
              ...(tab === 'support' ? getSupportTicketFilterParams(selectedSupportTicketFilter) : {}),
              ...(tab === 'support' && selectedSupportStatusId !== 'all' ? { status_id: selectedSupportStatusId } : {})
            }, cancelTokenSource)
            .then((response) => {
              if (currentRequest === requestCount) {
                if (Array.isArray(response?.data)) {
                  setUsers(response?.data.slice(0, 25))
                }
                else{
                  setUsers([])
                }
              }
            })
            .catch((error) => {
              if (!axios.isCancel(error) && currentRequest === requestCount) {
                setUsers([])
              }
            })
            .finally(() => {
              if (currentRequest === requestCount) {
                setLoadingSearch(false)
              }
            });
        }, 500)
      )
    }, 
    [selectedSupportStatusId, selectedSupportTicketFilter]
  );

  useEffect(() => {
    if (selectedTab === 'lesson') {
      setIsShowUsers(false)
      setUsers([])
      return undefined
    }

    debouncedGetUsers(valueSearch, selectedTab);

    return () => {
      debouncedGetUsers.cancel();
    };
  }, [valueSearch, selectedTab, selectedSupportStatusId, selectedSupportTicketFilter]);

  useEffect(() => {
    const unsubscribeSupportStatusChanged = websocket.subscribe('chat_support_status_changed', () => {
      if (selectedTab !== 'support' || !isTechSupport) return

      loadChats({ page: 1 })

      if (isShowUsers) {
        debouncedGetUsers(valueSearch, selectedTab)
      }
    })

    return () => {
      unsubscribeSupportStatusChanged?.()
    }
  }, [selectedTab, isTechSupport, isShowUsers, valueSearch, debouncedGetUsers, selectedSupportStatusId])

  useEffect(() => {
    const unsubscribeSupportTicketChanged = websocket.subscribe('chat_support_ticket_changed', (payload) => {
      if (selectedTab !== 'support' || !isTechSupport) return

      const ticketEvent = payload?.data?.chatId ? payload.data : payload
      updateSupportTicketFromEvent(ticketEvent)

      if (isShowUsers) {
        debouncedGetUsers(valueSearch, selectedTab)
      }
    })

    return () => {
      unsubscribeSupportTicketChanged?.()
    }
  }, [selectedTab, isTechSupport, isShowUsers, valueSearch, debouncedGetUsers, selectedSupportStatusId, selectedSupportTicketFilter])

  useEffect(() => {
    if (!lastMessageUpdate) return

    const updates = Array.isArray(lastMessageUpdate) ? lastMessageUpdate : [lastMessageUpdate]
    setChats((prev) => updates.reduce(
      (currentChats, update) => updateChatLastMessage(currentChats, update),
      prev,
    ))

    // Первое сообщение в новой переписке: чата в загруженном списке ещё нет — перечитываем первую страницу.
    const hasUnknownChat = updates.some((update) => (
      update?.chatId !== undefined &&
      !chats.some((chat) => Number(chat?.id) === Number(update.chatId))
    ))
    if (
      hasUnknownChat &&
      selectedFolderId === CHAT_FOLDER_ALL_ID &&
      ['all', 'direct'].includes(selectedTab || 'all')
    ) {
      loadChats({ page: 1 })
    }
  }, [lastMessageUpdate])

  useEffect(() => {
    const unsubscribeNotification = websocket.subscribe('notification', (payload) => {
      if (payload?.notification?.type !== 'chat_new_message') return

      const notification = payload.notification

      if (
        typeof notification?.chatId === 'undefined' ||
        typeof notification?.message !== 'string'
      ) {
        return
      }

      setChats((prev) => updateChatLastMessage(prev, notification))
    })

    return () => {
      unsubscribeNotification?.()
    }
  }, [])

  const handleCloseUsersList = () => {
    setIsShowUsers(false)
    reset()
  }

  const handleCollapseBtnClick = () => setPage('default')
  const chatBackgroundColor = useSelector(store => store.settings.chatBackgroundColor);
  const openFoldersModal = (selectedFolderId = null) => {
    dispatch(openModal({
      type: TYPES.CHAT_FOLDERS,
      data: {
        ...(selectedFolderId !== null && selectedFolderId !== undefined ? { selectedFolderId } : {}),
        onSuccess: () => {
          getFolders()
          loadChats({ page: 1 })
        }
      }
    }))
  }

  const folderTabs = useMemo(() => ([
    { id: CHAT_FOLDER_ALL_ID, title: 'Все чаты' },
    ...folders
  ]), [folders])

  const supportStatusOptions = useMemo(() => ([
    { value: 0, label: 'Без статуса' },
    ...supportStatuses.map((status) => ({
      value: status?.id,
      label: status?.title,
    })),
  ]), [supportStatuses])

  const supportStatusFilterOptions = useMemo(() => ([
    { value: 'all', label: 'Все статусы' },
    ...supportStatusOptions,
  ]), [supportStatusOptions])

  const supportManagerOptions = useMemo(() => (
    techSupportUserIds.map((id) => {
      const manager = supportManagers.find((item) => Number(item?.id) === Number(id))

      return {
        value: id,
        label: manager ? (formateName(manager.name, 'F I') || manager.name || `ID ${id}`) : `ID ${id}`,
      }
    })
  ), [supportManagers, techSupportUserIds])

  const supportTicketFilterOptions = useMemo(() => ([
    { value: SUPPORT_TICKET_ASSIGNMENT_DEFAULT, label: 'Мои + неразобранные' },
    { value: SUPPORT_TICKET_ASSIGNMENT_UNASSIGNED, label: 'Неразобранные' },
    { value: SUPPORT_TICKET_ASSIGNMENT_MINE, label: 'Мои' },
    { value: SUPPORT_TICKET_ASSIGNMENT_ALL, label: 'Все' },
    ...supportManagerOptions.map((manager) => ({
      value: `${SUPPORT_TICKET_MANAGER_PREFIX}${manager.value}`,
      label: `Менеджер: ${manager.label}`,
    })),
  ]), [supportManagerOptions])

  const handleAssignSupportTicketManager = async (chatId, managerId) => {
    const response = await chatApi.assignSupportTicketManager(chatId, {
      manager_id: managerId,
    })

    if (response?.data?.status !== 'success') {
      addAlert({ type: 'error', title: 'Не удалось назначить менеджера' })
      return
    }

    const manager = supportManagers.find((item) => Number(item?.id) === Number(managerId)) ?? null

    setChats((prev) => prev.map((chat) => (
      Number(chat?.id) === Number(chatId)
        ? {
            ...chat,
            supportTicket: {
              ...(chat?.supportTicket || {}),
              managerId,
              manager: response?.data?.data?.supportTicket?.manager ?? manager,
              isClosed: false,
            },
          }
        : chat
    )))

    loadChats({ page: 1 })
  }

  return (
    <div className={cn(s.page, isFullPage && s.pageFull)}>
      <div className={s.tabsWrapper}>
        {showChatListModeTabs ? (
          <div className={cn(s.tabs, s.chatListModeTabs, 'custom-scroll')}>
            <Button
              action={() => onChatListModeChange?.('internal')}
              className={cn(
                s.tabButton,
                s.chatListModeTab,
                chatListMode === 'internal' && s.tabActive,
              )}
              newDesign
            >
              Внутренние чаты
            </Button>
            <Button
              action={() => onChatListModeChange?.('integration')}
              className={cn(
                s.tabButton,
                s.chatListModeTab,
                chatListMode === 'integration' && s.tabActive,
              )}
              newDesign
            >
              <span className={s.chatListModeTabLabel}>Интеграционные чаты</span>
              {integrationUnreadBadge ? (
                <span className={s.chatListModeBadge} aria-hidden>
                  {integrationUnreadBadge}
                </span>
              ) : null}
            </Button>
          </div>
        ) : null}
        {!isIntegrationMode ? (
          <>
          <div className={s.topBar}>
        <div className={cn(s.tabs, s.mainTabs, 'custom-scroll')}>
          {
            !chatIsDisabledButTechnicalSupportIsEnabled && (
              <>
                <Button
                  action={() => handlePlatformTabSelect('all')}
                  className={cn(s.tabButton, selectedTab === 'all' && s.tabActive)}
                  newDesign
                >
                  Все
                </Button>
                <Button
                  action={() => handlePlatformTabSelect('direct')}
                  className={cn(s.tabButton, selectedTab === 'direct' && s.tabActive)}
                  newDesign
                >
                  Личные
                </Button>
                <Button
                  action={() => handlePlatformTabSelect('group')}
                  className={cn(s.tabButton, selectedTab === 'group' && s.tabActive)}
                  newDesign
                >
                  Группы
                </Button>
                {notificationChatsEnabled && hasNotificationChats && (
                  <Button
                    action={() => handlePlatformTabSelect('notification')}
                    className={cn(s.tabButton, selectedTab === 'notification' && s.tabActive)}
                    newDesign
                  >
                    {notificationChatsSectionTitle}
                  </Button>
                )}
              </>
            )
          }
          {
            canSeeLessonChats && (hasLessonChats || selectedTab === 'lesson') && (
              <Button
                action={() => handlePlatformTabSelect('lesson')}
                className={cn(s.tabButton, selectedTab === 'lesson' && s.tabActive)}
                newDesign
              >
                Чаты уроков
              </Button>
            )
          }
          {
            technicalSupportIsEnabled && (
              <Button
                action={async () => {
                  // если учасник адмитнов тех. поддержки
                  if (isTechSupport) {
                    history.push('/helpdesk')
                    return
                  }

                  const response = await chatApi.createSupportChat({
                    user_id: userId,
                  })

                  if (response.data?.data?.chat_id) {
                    setPage('inChat', response.data?.data?.chat_id)
                  }
                }}
                className={cn(s.tabButton, selectedTab === 'support' && s.tabActive)}
                newDesign
              >
                Тех. поддержка
              </Button>
            )
          }
          
        </div>
        <button
          type='button'
          className={cn(s.collapseBtn, isFullPage && s.hidden)}
          title='Свернуть чат'
          aria-label='Свернуть чат'
          onClick={handleCollapseBtnClick}
        >
          <CloseIcon width='16' height='16' />
        </button>
        </div>
        {
          isChatsCanBeGroupedIntoFolders && selectedTab !== 'support' && selectedTab !== 'lesson' && (
            <div className={cn(s.tabs, s.folderTabs, 'custom-scroll')}>
              <TooltipWrapper title='Настройки папок'>
                <button
                  className={s.addFolderBtn}
                  type="button"
                  onClick={() => openFoldersModal()}
                >
                  <AddNewChatIcon width={'28'} height={'28'} />
                </button>
              </TooltipWrapper>
              
              {folderTabs.map((folder) => (
                <Button
                  key={folder.id}
                  action={() => setSelectedFolder({folderId: folder.id})}
                  className={cn(
                    s.tabButton,
                    s.folderTabButton,
                    selectedFolderId === folder.id && s.tabActive
                  )}
                  newDesign
                >
                  <span className={s.folderTabTitle}>{folder.title}</span>
                  {folder.id !== CHAT_FOLDER_ALL_ID && (
                    <span
                      className={s.folderEditBtn}
                      onClick={(event) => {
                        event.stopPropagation()
                        openFoldersModal(folder.id)
                      }}
                    >
                      <EditIcon width={'12'} height={'12'} />
                    </span>
                  )}
                </Button>
              ))}
            </div>
          )
        }
          </>
        ) : (
          integrationHasConnectedProviders ? belowTabsSlot : null
        )}
      </div>
      {isIntegrationMode ? (
        !integrationHasConnectedProviders ? (
          <div className={s.integrationListEmpty}>
            <div className={s.integrationListEmptyTitle}>
              Интеграции не подключены
            </div>
            <div className={s.integrationListEmptyText}>
              Подключите их в{' '}
              <Link className={s.integrationListEmptyLink} to="/crm-settings?tab=6">
                настройках CRM
              </Link>
              .
            </div>
          </div>
        ) : integrationListSlot ? (
          <div className={s.integrationListSlot}>{integrationListSlot}</div>
        ) : (
          <div className={s.integrationListEmpty}>
            Выберите канал интеграции: VK, MAX или Telegram
          </div>
        )
      ) : (
        <>
      {selectedTab !== 'lesson' && <div className={s.header}>
        {canStartChat() && (
          <>
            <Search
              placeholder={(selectedTab == 'group' || selectedTab == 'support') ? 'Введите название чата' : selectedTab == 'all' ? 'Поиск' : directSearchPlaceholder}
              className={s.search}
              bind={bindSearch}
              withClose={false}
              onFocus={() => setIsShowUsers(true)}
            />
            {isShowUsers && (
              <button 
                style={{background: !!chatBackgroundColor?.trim() ? `${chatBackgroundColor}` : 'var(--color-button-neutral-bg)'}}
                className={s.close} onClick={handleCloseUsersList}>
                <CloseIcon />
              </button> 
            )}
            <div className={s.headerActions}>
              <button className={s.addBtn} title='Создать чат' aria-label='Создать чат' onClick={() => setPage('create')}>
                <AddNewChatIcon width={'36'} height={'36'} />
              </button>
              <span className={s.desc}>Создать чат</span>
            </div>
          </>
        )}
      </div>}
      <div className={classNames(s.header, s.headerSupport)}>
        {selectedTab === 'support' && isTechSupport && (
          <>
            <CompactSelect
              value={selectedSupportTicketFilter}
              options={supportTicketFilterOptions}
              onChange={setSelectedSupportTicketFilter}
              className={s.supportFilterSelect}
            />
            <CompactSelect
              value={selectedSupportStatusId}
              options={supportStatusFilterOptions}
              onChange={setSelectedSupportStatusId}
              className={s.supportFilterSelect}
            />
          </>
        )}
        {selectedTab === 'lesson' && (
          <>
            <CompactSelect
              value={selectedLessonChatKind}
              options={LESSON_CHAT_KIND_OPTIONS}
              onChange={setSelectedLessonChatKind}
              className={s.lessonFilterSelect}
            />
            <CompactSelect
              value={selectedLessonCourseId}
              options={lessonCourseOptions}
              onChange={handleSelectLessonCourse}
              className={s.lessonFilterSelect}
              searchable
              searchPlaceholder="Поиск курса"
            />
            <CompactSelect
              value={selectedLessonBlockId}
              options={lessonBlockOptions}
              onChange={handleSelectLessonBlock}
              className={s.lessonFilterSelect}
              searchable
              searchPlaceholder="Поиск блока"
            />
            <CompactSelect
              value={selectedLessonThemeId}
              options={lessonThemeOptions}
              onChange={handleSelectLessonTheme}
              className={s.lessonFilterSelect}
              searchable
              searchPlaceholder="Поиск темы"
            />
            <CompactSelect
              value={selectedLessonLessonId}
              options={lessonLessonOptions}
              onChange={setSelectedLessonLessonId}
              className={s.lessonFilterSelect}
              searchable
              searchPlaceholder="Поиск урока"
            />
            <CompactSelect
              value={selectedLessonGroupId}
              options={lessonGroupOptions}
              onChange={setSelectedLessonGroupId}
              className={s.lessonFilterSelect}
              searchable
              searchPlaceholder="Поиск группы"
            />
            {activeLessonFiltersCount > 0 && (
              <button
                type="button"
                className={s.lessonFilterReset}
                onClick={resetLessonFilters}
                title="Сбросить фильтры"
              >
                Сбросить
                <span className={s.lessonFilterResetCount}>{activeLessonFiltersCount}</span>
              </button>
            )}
          </>
        )}
      </div>
      {
        canStartChat() && isShowUsers && (
          loadingSearch ? 
            (
              <div className={s.preloaderWrapper}><Preloader /></div>
            ) :
            (
              <UserList users={users} setPage={setPage} activeUsers={activeUsers} />
            )
        )
      }
      {!isShowUsers && (
        <>
          <ChatViewList
            chats={chats}
            setPage={openChatFromList}
            setIsNeedUpdate={() => loadChats({page: 1})}
            unReadChats={activeUnReadChats}
            isTechSupport={isTechSupport}
            supportStatusOptions={supportStatusOptions}
            supportManagerOptions={supportManagerOptions}
            onChangeSupportStatus={handleChangeSupportStatus}
            onAssignSupportTicketManager={handleAssignSupportTicketManager}
            onLoadMore={handleLoadMore}
            onReadStateChange={handleReadStateChange}
          />
        </>
      )}
        </>
      )}
    </div>
  )
}

export default MainChatPage
